import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Typography, Box } from "@mui/material";
import StyledTooltip from "../../../StyledTooltip/StyledTooltip";
import CarrierBookPill from "../../../Pills/CarrierBookPill/CarrierBookPill";
import DialButton from "../../../Buttons/DialButton/DialButton";
import WarningMessage from "../../../WarningMessage/WarningMessage";
import { formatEmpty } from "../../../../utils/formatHelpers";
import { nullCheck } from "../../../../utils/dataHelpers";

export default function CarrierNameCell({ cell, column, actionCallback }) {
  const key = nullCheck(cell, "id", "column", "idKey");
  const carrierId = nullCheck(cell, "", "row", "original", key);
  const phoneNumber = nullCheck(cell, null, "row", "original", "carrierPhoneNumber");
  const noDispatch = nullCheck(cell, null, "row", "original", "noDispatch");
  const isActive = nullCheck(cell, true, "row", "original", "active");
  const active = nullCheck(cell, false, "row", "original", "activeRow");
  const originPoint = nullCheck(column, null, "originPoint");
  const keyword = nullCheck(cell, null, "column", "keyword");
  const textArray = cell.value && cell.value.split(RegExp(keyword, "ig"));
  const match = cell.value && cell.value.match(RegExp(keyword, "ig"));

  // Hint Performance Metrics
  const showMetrics = nullCheck(column, false, "showMetrics");
  const orders = nullCheck(cell, "0", "row", "original", "orders");
  const otp = nullCheck(cell, "0%", "row", "original", "otp");
  const otd = nullCheck(cell, "0%", "row", "original", "otd");
  const bounceCount = nullCheck(cell, "0", "row", "original", "bounceCount");
  const bounce = nullCheck(cell, "0%", "row", "original", "bounce");
  const sameDayBounce = nullCheck(cell, "0%", "row", "original", "sameDayBounce");
  const techTracking = nullCheck(cell, "0%", "row", "original", "techTracking");

  // Hint Ready to book
  const showCanBook = nullCheck(column, false, "showCanBook");
  const [carrierName, setCarrierName] = useState("");
  const [carrierStatus, setCarrierStatus] = useState("");
  const [highwayStatus, setHighwayStatus] = useState("");
  const [highwayRuleAssessment, setHighwayRuleAssessment] = useState("");

  useEffect(() => {
    setCarrierName(nullCheck(cell, "", "row", "original", "carrierName"));
    setHighwayStatus(nullCheck(cell, "", "row", "original", "highwayStatus"));
    setHighwayRuleAssessment(nullCheck(cell, "", "row", "original", "highwayRuleAssessment"));

    if (originPoint === "offers-dashboard" || originPoint === "load-matching") {
      setCarrierStatus(nullCheck(cell, "", "row", "original", "carrierStatus"));
    } else if (originPoint === "load-history" || originPoint === "carrier-search") {
      const status = nullCheck(cell, "", "row", "original", "carrierActive");
      setCarrierStatus(status ? "A" : "");
    }
  }, [cell]);

  return (
    <span style={{ display: "flex", alignItems: "center" }}>
      {showCanBook &&
        <CarrierBookPill
          carrierName={carrierName}
          carrierStatus={carrierStatus}
          highwayStatus={highwayStatus}
          highwayRuleAssessment={highwayRuleAssessment}
          icon
        />
      }
      {!keyword &&
        <Box sx={{ display: "flex", alignItems: "center", marginRight: "8px", height: "20px" }}>
          <DialButton
            number={phoneNumber}
            icon="phone"
            disabled={!phoneNumber}
            color={active ? "white" : "blue"}
          />
        </Box>
      }
      {keyword ?
        (
          <Typography
            variant="body2"
            className="Customized-table-overflow-cell"
            sx={{
              ...(isActive ? { color: "primary.60" } : { color: "black_solid.60" }),
              "&:hover": {
                cursor: "pointer"
              }
            }}
            onClick={e => {
              e.stopPropagation();
              carrierId && actionCallback({ action: "carrier-profile", carrierId: carrierId });
            }}
          >
            {textArray.map((item, index) => (
              <span key={index}>
                {item}
                {index !== textArray.length - 1 && match && (
                  <b>{match[index]}</b>
                )}
              </span>
            ))}
          </Typography>
        ) : (
          <StyledTooltip
            title={!showMetrics && cell.value ? cell.value.toUpperCase() : ""}
            titleObject={showMetrics ?
              <Box>
                <Typography variant="body1" sx={{ marginBottom: "10px" }}>
                  {`Orders: ${orders}`}
                </Typography>
                <Typography variant="body1" sx={{ marginBottom: "10px" }}>
                  {`On Time Pickup: ${otp}`}
                </Typography>
                <Typography variant="body1" sx={{ marginBottom: "10px" }}>
                  {`On Time Delivery: ${otd}`}
                </Typography>
                <Typography variant="body1" sx={{ marginBottom: "10px" }}>
                  {`Bounce Count: ${bounceCount}`}
                </Typography>
                <Typography variant="body1" sx={{ marginBottom: "10px" }}>
                  {`Bounce: ${bounce}`}
                </Typography>
                <Typography variant="body1" sx={{ marginBottom: "10px" }}>
                  {`Same day Bounce: ${sameDayBounce}`}
                </Typography>
                <Typography variant="body1">
                  {`Tech Tracking: ${techTracking}`}
                </Typography>
              </Box> : null
            }
          >
            <Typography
              variant="body1"
              className="Customized-table-overflow-cell"
              sx={{
                ...(isActive ? { color: active ? "white_opacity.100" : "primary.60" } : { color: "black_solid.60" }),
                "&:hover": {
                  cursor: "pointer"
                }
              }}
              onClick={e => {
                e.stopPropagation();
                carrierId && actionCallback({ action: "carrier-profile", carrierId: carrierId });
              }}
            >
              {formatEmpty(cell.value).toUpperCase()}
            </Typography>
          </StyledTooltip>
        )
      }
      {noDispatch &&
        <Box sx={{ margin: "-3px 0 0 4px" }}>
          <WarningMessage text="Not approved for dispatch" />
        </Box>
      }
    </span>
  );
}

CarrierNameCell.propTypes = {
  cell: PropTypes.object,
  column: PropTypes.object,
  actionCallback: PropTypes.func
};